import React from "react";
import { Link } from "react-router-dom";
import { Check, Home as HomeIcon, KeyRound, ArrowRight } from "lucide-react";
import Reveal from "../Reveal";

const SELLING = [
  "Prices your home with a full market analysis, not a guess",
  "Delivers professional photography and video. 78 percent of sellers say quality photos matter most when they pick an agent",
  "Negotiates offers hard while protecting your goals and timeline",
  "Communicates often, so you always know where your sale stands",
  "Knows the local market in detail, including school zones, buyer demand, and neighborhood trends",
];

const BUYING = [
  "Listens first, then narrows the search to homes fitting your life and budget",
  "Knows local trends, including school zones, commute times, and which streets hold value",
  "Negotiates with a clear strategy, especially in a competitive market",
  "Flags inspection issues and contract concerns before they cost you money",
  "Stays responsive and keeps you updated from the first showing through closing",
];

const AgentCard = ({ icon: Icon, kicker, title, intro, items }) => (
  <div className="bg-white rounded-sm p-8 md:p-10 h-full shadow-[0_18px_45px_-30px_rgba(58,55,54,0.4)]">
    <div className="flex items-center gap-4 mb-5">
      <span className="flex items-center justify-center w-12 h-12 rounded-full bg-[#efe4e5] shrink-0">
        <Icon size={22} className="text-[#6f5f62]" />
      </span>
      <div>
        <p className="font-sans-el tracking-[0.25em] uppercase text-[#9a878b] text-xs">{kicker}</p>
        <h3 className="font-serif-el text-2xl md:text-3xl text-[#3a3736] leading-tight">{title}</h3>
      </div>
    </div>
    <p className="font-serif-el text-lg leading-relaxed text-[#4a4644] mb-6">{intro}</p>
    <ul className="space-y-3">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3">
          <Check size={17} className="text-[#b6a4a7] mt-1 shrink-0" />
          <span className="font-sans-el text-[#4a4644] leading-relaxed">{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

const AgentGuide = () => (
  <section className="bg-[#efe8df] py-20 md:py-28">
    <div className="max-w-[1200px] mx-auto px-6">
      <Reveal className="text-center mb-14 max-w-3xl mx-auto">
        <p className="font-sans-el tracking-[0.4em] uppercase text-[#9a878b] text-xs mb-4">
          A Helpful Guide
        </p>
        <h2 className="section-title text-3xl md:text-4xl text-[#3a3736] leading-tight">
          Choosing the Right Agent to Buy or Sell Your Home in Chester and Delaware County
        </h2>
        <div className="divider-ornament mt-6 mb-6">
          <span className="line" /><span className="dot" /><span className="line" />
        </div>
        <p className="font-serif-el text-xl leading-relaxed text-[#4a4644]">
          Choosing the right agent matters when you buy or sell a home in Chester or Delaware County,
          Pennsylvania. Here is what to look for on both sides of the transaction.
        </p>
      </Reveal>

      <div className="grid md:grid-cols-2 gap-8">
        <Reveal>
          <AgentCard
            icon={HomeIcon}
            kicker="For Sellers"
            title="What Makes a Good Selling Agent"
            intro="A strong selling agent prices your home right from the start and builds a marketing plan reaching serious buyers fast. Home values in Chester and Delaware County shift street by street, so your agent needs sharp knowledge of recent sales, buyer demand, and neighborhood trends. Sellers who hire an experienced agent get stronger offers and shorter time on market, since accurate pricing and strong presentation set the tone before a buyer walks through the door."
            items={SELLING}
          />
        </Reveal>
        <Reveal>
          <AgentCard
            icon={KeyRound}
            kicker="For Buyers"
            title="What Makes a Good Buying Agent"
            intro="A good buying agent starts by learning what you need, not filling an open showing schedule. National Association of Realtors research shows nine in ten buyers rate market knowledge and quick responses as top priorities. The same holds for buyers in Chester and Delaware County. Your agent should catch problems early, negotiate hard against listing agents, and keep you informed at every step, from the first showing to closing day."
            items={BUYING}
          />
        </Reveal>
      </div>

      <Reveal className="text-center mt-14">
        <Link
          to="/contact"
          className="btn-mauve inline-flex items-center gap-2 px-8 py-4 rounded-sm"
        >
          Let's Talk <ArrowRight size={16} />
        </Link>
      </Reveal>
    </div>
  </section>
);

export default AgentGuide;
